// gsaExport.js — Excel sheet (same layout as the lab's "GSA" sheet) + grading-curve graph for the grain size analysis.
import { FINE_LABELS } from './gsaCalc';

const THIN = { style: 'thin', color: { argb: 'FF000000' } };
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const font = (size = 10, bold = false) => ({ name: 'Arial', size, bold });
const utcDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

// ---------------------------------------------------------------- graph
// Grading curve: % passing vs particle size (log scale, coarse on the left). Works on any canvas (browser or node).
export function drawGradation(canvas, { points, title }) {
  const W = 1600, H = 760;
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);

  const m = { l: 120, r: 50, t: 110, b: 110 };
  const L0 = Math.log10(100), L1 = Math.log10(0.01);
  const px = (s) => m.l + ((L0 - Math.log10(s)) / (L0 - L1)) * (W - m.l - m.r);
  const py = (v) => H - m.b - (v / 100) * (H - m.t - m.b);

  g.fillStyle = '#111'; g.font = 'bold 32px Arial'; g.textAlign = 'center';
  g.fillText(title, W / 2, 48);

  // soil-fraction bands + labels
  const band = (a, b, label, fill) => {
    g.fillStyle = fill; g.fillRect(px(a), m.t, px(b) - px(a), H - m.t - m.b);
    g.fillStyle = '#374151'; g.font = 'bold 22px Arial'; g.textAlign = 'center';
    g.fillText(label, (px(a) + px(b)) / 2, m.t - 14);
  };
  band(75, 4.75, 'GRAVEL', '#f6f8fc'); band(4.75, 0.075, 'SAND', '#fcfaf2'); band(0.075, 0.01, 'SILT & CLAY', '#f6f8fc');

  // grid + tick labels
  g.lineWidth = 1; g.strokeStyle = '#d4d9e2'; g.fillStyle = '#333'; g.font = '22px Arial'; g.textAlign = 'center';
  [100, 75, 19, 4.75, 2, 0.425, 0.075, 0.01].forEach((s) => {
    g.beginPath(); g.moveTo(px(s), m.t); g.lineTo(px(s), H - m.b); g.stroke();
    g.textAlign = s === 100 ? 'right' : s === 75 ? 'left' : 'center'; // 100 and 75 sit close together
    g.fillText(String(s), px(s), H - m.b + 32);
  });
  g.textAlign = 'right';
  for (let v = 0; v <= 100; v += 10) {
    g.beginPath(); g.moveTo(m.l, py(v)); g.lineTo(W - m.r, py(v)); g.stroke();
    g.fillText(String(v), m.l - 12, py(v) + 8);
  }
  g.strokeStyle = '#111'; g.lineWidth = 2.5; g.strokeRect(m.l, m.t, W - m.l - m.r, H - m.t - m.b);
  g.fillStyle = '#111'; g.font = '26px Arial'; g.textAlign = 'center';
  g.fillText('Particle size (mm) — log scale', (m.l + W - m.r) / 2, H - 22);
  g.save(); g.translate(38, (m.t + H - m.b) / 2); g.rotate(-Math.PI / 2); g.fillText('% passing', 0, 0); g.restore();

  // curve + points
  g.strokeStyle = '#1d4ed8'; g.lineWidth = 4; g.beginPath();
  points.forEach((p, i) => (i ? g.lineTo(px(p.size), py(p.pass)) : g.moveTo(px(p.size), py(p.pass))));
  g.stroke();
  g.fillStyle = '#111';
  points.forEach((p) => { g.beginPath(); g.arc(px(p.size), py(p.pass), 9, 0, Math.PI * 2); g.fill(); });
  return canvas;
}

// ---------------------------------------------------------------- sheet
export function addGSASheet(wb, { header, inputs, result, imageBase64 }) {
  const ws = wb.addWorksheet('GSA', {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
  });
  [13.4, 14.6, 19.3, 15.9, 18, 19.1, 11.3].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

  const center = { horizontal: 'center', vertical: 'middle', wrapText: true };
  const left = { horizontal: 'left', vertical: 'middle', wrapText: true };
  const area = (r1, c1, r2, c2, value, o = {}) => {
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
      const cell = ws.getCell(r, c);
      cell.border = o.border ?? BOX; cell.font = o.font ?? font(); cell.alignment = o.align ?? center;
    }
    const top = ws.getCell(r1, c1);
    if (value !== undefined && value !== null && value !== '') top.value = value;
    if (o.numFmt) top.numFmt = o.numFmt;
    if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  };
  const F = (formula, res) => ({ formula, result: res });

  [33.75, 21, 16.5, 12, 8, 20.25, 20.25, 20.25, 20.25, 20.25, 20.25, 18, 18].forEach((h, i) => { ws.getRow(i + 1).height = h; });
  for (let r = 14; r <= 21; r++) ws.getRow(r).height = 24.75;
  for (let r = 29; r <= 52; r++) ws.getRow(r).height = 12;

  area(1, 1, 1, 2, 'Name of Project', { font: font(11, true), align: left });
  area(1, 3, 1, 7, header.project, { font: font(11), align: left });
  area(2, 1, 2, 2, 'Issue to ', { font: font(11, true), align: left });
  area(2, 3, 2, 7, header.issueTo, { font: font(11), align: left });
  area(3, 1, 3, 7, 'SIEVE ANALYSIS', { font: font(12, true) });
  area(4, 1, 4, 7, '(As per IS :2720: Part 4:1985)', { font: font(10, true) });

  const info = (r, l1, v1, l2, v2, isDate) => {
    area(r, 1, r, 2, l1, { align: left }); area(r, 3, r, 4, v1, { align: left });
    area(r, 5, r, 5, l2, { align: left }); area(r, 6, r, 7, v2, isDate ? { numFmt: 'dd/mm/yyyy' } : {});
  };
  info(6, 'Source/Location', header.location, 'Date of Receiving', utcDate(header.receivedOn), true);
  info(7, 'Kind of material', header.material, 'Date of Testing', utcDate(header.testedOn), true);
  info(8, 'Sample No.', header.sampleNo, 'Tested by', header.testedBy);

  // total sample / moisture
  const T = inputs.totalDryG;
  const hasWet = inputs.wetG != null;
  area(9, 1, 9, 2, 'Weight wet Soil', { align: left });
  area(9, 3, 9, 4, hasWet ? inputs.wetG : '', { numFmt: '0.00' });
  area(9, 5, 9, 5, 'Weight of dry Soil', { align: left });
  area(9, 6, 9, 7, F('E11', T), { numFmt: '0.00' });
  area(10, 1, 10, 2, 'Weight of Water', { align: left });
  area(10, 3, 10, 4, hasWet ? F('C9-F9', inputs.wetG - T) : '', { numFmt: '0.00' });
  area(10, 5, 10, 5, 'N.M.C(%)', { align: left });
  area(10, 6, 10, 7, hasWet ? F('C10/F9*100', result.nmcPct) : '', { numFmt: '0.0' });
  area(11, 3, 11, 4, 'Weight of Total Sample (gms) =', { align: left });
  area(11, 5, 11, 5, T, { font: font(10, true), numFmt: '0.00' });

  // table header
  ['IS Sieve Size', 'wt.retained (gms)', 'wt. of cumulative retained (gms)', '%  of Cumulative retained wt', '% of  Passing']
    .forEach((t, i) => area(12, i + 1, 13, i + 1, t, { font: font(10, true) }));
  area(12, 6, 13, 7, 'Remarks', { font: font(10, true) });

  // rows 14..21: 100, 75, 19, 4.75 | 2.00, 425, 75 | pan
  const names = ['100 mm', '75.0 mm', '19.0 mm', '4.75 mm', '2.00 mm', '425 Mic', '75 Mic', 'Pan'];
  const scaled = result.scaledFineG;
  const retained = [...inputs.coarseRetainedG, ...scaled, T - [...inputs.coarseRetainedG, ...scaled].reduce((a, b) => a + b, 0)];
  let cum = 0;
  names.forEach((nm, i) => {
    const r = 14 + i;
    cum += retained[i];
    area(r, 1, r, 1, nm, { align: left });
    let bVal;
    if (i < 4) bVal = retained[i];
    else if (i < 7) {
      const src = ['C25', 'F25', 'C26'][i - 4];
      bVal = F(`${src}*($E$11-$C$17)/$F$24`, retained[i]);
    } else bVal = F('$E$11-C20', retained[i]);
    area(r, 2, r, 2, bVal, { numFmt: '0.00' });
    area(r, 3, r, 3, i === 0 ? F('B14', cum) : F(`C${r - 1}+B${r}`, cum), { numFmt: '0.00' });
    area(r, 4, r, 4, F(`C${r}/$E$11*100`, (cum / T) * 100), { numFmt: '0.0' });
    area(r, 5, r, 5, F(`100-D${r}`, 100 - (cum / T) * 100), { numFmt: '0.0' });
  });

  // remarks (fractions)
  const rem = [
    ['Cobble %', '100-E15', result.cobblePct], ['GRAVEL % :  ', 'E15-E17', result.gravelPct],
    ['SAND %:  ', 'E17-E20', result.sandPct], ['COARSE SAND %', 'E17-E18', result.coarseSandPct],
    ['MEDIUM SAND %', 'E18-E19', result.mediumSandPct], ['FINE SAND%', 'E19-E20', result.fineSandPct],
    ['SILT & CLAY SIZE %', 'E20', result.finesPct],
  ];
  rem.forEach(([label, fm, val], i) => {
    area(14 + i, 6, 14 + i, 6, label, { align: left });
    area(14 + i, 7, 14 + i, 7, F(fm, val), { font: font(10, true), numFmt: '0.0' });
  });
  area(21, 6, 21, 7, '', {});

  // actual sub-sample readings (what was really weighed for the fine sieves)
  const p = inputs.partial;
  area(23, 1, 23, 7, 'Fine fraction — soil passing 4.75 mm (actual sub-sample readings)', { font: font(10, true), align: left });
  area(24, 1, 24, 2, 'Sieving method', { align: left });
  area(24, 3, 24, 4, p.method === 'dry' ? 'Dry sieving' : 'Wet sieving');
  area(24, 5, 24, 5, 'Weight of sub-sample (gms)', { align: left });
  area(24, 6, 24, 7, p.massG, { numFmt: '0.00' });
  area(25, 1, 25, 2, `Retained on ${FINE_LABELS[0]} (gms)`, { align: left });
  area(25, 3, 25, 4, p.retainedG[0], { numFmt: '0.00' });
  area(25, 5, 25, 5, `Retained on ${FINE_LABELS[1]} (gms)`, { align: left });
  area(25, 6, 25, 7, p.retainedG[1], { numFmt: '0.00' });
  area(26, 1, 26, 2, `Retained on ${FINE_LABELS[2]} (gms)`, { align: left });
  area(26, 3, 26, 4, p.retainedG[2], { numFmt: '0.00' });
  area(26, 5, 26, 5, 'Pan (gms)', { align: left });
  area(26, 6, 26, 7, p.method === 'dry' ? p.panG : '—', { numFmt: '0.00' });
  area(27, 1, 27, 7,
    'Weights shown against 2.00 mm, 425 µ and 75 µ in the table are scaled to the whole sample: reading × (total sample − retained on 4.75 mm) ÷ sub-sample weight.',
    { font: font(8), align: left, border: {} });

  if (imageBase64) {
    const id = wb.addImage({ base64: imageBase64, extension: 'png' });
    ws.addImage(id, { tl: { col: 0, row: 28 }, ext: { width: 800, height: 380 } });
  }
  area(54, 1, 56, 7, 'Authorised Signatory', { font: font(14, true), align: { horizontal: 'right', vertical: 'middle' }, border: {} });
  return ws;
}

// ---------------------------------------------------------------- portal hook
export const gsaExporter = {
  async addSheet(wb, { header, data }) {
    const { inputs, result } = data;
    const points = result.rows.map((r) => ({ size: r.size, pass: r.pctPassing }));
    const canvas = drawGradation(document.createElement('canvas'), {
      points, title: `Grading curve — ${header.sampleNo || ''} ${header.location || ''}`.trim(),
    });
    addGSASheet(wb, { header, inputs, result, imageBase64: canvas.toDataURL('image/png') });
    const png = await new Promise((res) => canvas.toBlob(res, 'image/png'));
    return [{ suffix: 'GSA_grading_curve.png', blob: png }];
  },
};
